import { CaretDown, CaretUp } from "@phosphor-icons/react";
import { m } from "@plannello/i18n/messages";
import type { ReactNode } from "react";
import { Cell, Column, Row, type SortDescriptor, Table, TableBody, TableHeader } from "react-aria-components";
import { cx } from "@/lib/cx";
import { Skeleton } from "./Skeleton";

export type { SortDescriptor };

export type DataColumn<T> = {
  id: string;
  header: string;
  cell: (row: T) => ReactNode;
  /** The cell that names the row for screen readers (usually the first column). */
  isRowHeader?: boolean;
  allowsSorting?: boolean;
  align?: "start" | "end";
  /** Width and responsive visibility, e.g. "w-40" or "hidden lg:table-cell". */
  className?: string;
  /** Actions column: header text is kept for assistive tech only. */
  hideHeader?: boolean;
};

/** Class strings shared with hand-built tables (audit log) so every table in the product looks alike. */
export const dataTableStyles = {
  frame: "overflow-x-auto rounded-panel border border-line bg-surface shadow-[0_1px_2px_var(--shadow-soft)]",
  table: "w-full border-collapse text-sm",
  head: "border-b border-line bg-subtle",
  th: "h-8 px-3 text-left text-xs font-medium whitespace-nowrap text-fg-muted",
  row: "border-b border-line last:border-b-0",
  td: "px-3 py-2 align-middle",
} as const;

const rowStyle = cx(
  dataTableStyles.row,
  "outline-none data-hovered:bg-hover data-pressed:bg-active",
  "data-focus-visible:outline-2 data-focus-visible:-outline-offset-2 data-focus-visible:outline-solid data-focus-visible:outline-focus",
);

/**
 * Data table on React Aria's Table: keyboard grid navigation, Enter or click on a row calls
 * `onRowAction` (open the detail drawer), optional sortable headers (the caller sorts), a built-in
 * empty state. Columns are config, cells are render functions. Rows need a string `id`.
 *
 *   <DataTable label="Users" rows={users} columns={cols} onRowAction={(u) => open(u.id)}
 *     renderEmptyState={() => <EmptyState ... />} />
 *
 * Width: put the table in a container; it scrolls horizontally inside its frame on narrow screens.
 * Hide secondary columns below a breakpoint with `className: "hidden lg:table-cell"`.
 */
export function DataTable<T extends { id: string }>({
  label,
  columns,
  rows,
  onRowAction,
  sortDescriptor,
  onSortChange,
  renderEmptyState,
  className,
}: {
  label: string;
  columns: DataColumn<T>[];
  rows: T[];
  onRowAction?: (row: T) => void;
  sortDescriptor?: SortDescriptor;
  onSortChange?: (d: SortDescriptor) => void;
  renderEmptyState?: () => ReactNode;
  className?: string;
}) {
  return (
    <div className={cx(dataTableStyles.frame, className)}>
      <Table
        aria-label={label}
        className={dataTableStyles.table}
        sortDescriptor={sortDescriptor}
        onSortChange={onSortChange}
        onRowAction={
          onRowAction
            ? (key) => {
                const row = rows.find((r) => r.id === key);
                if (row) onRowAction(row);
              }
            : undefined
        }
      >
        <TableHeader columns={columns} className={dataTableStyles.head}>
          {(col) => (
            <Column
              id={col.id}
              isRowHeader={col.isRowHeader}
              allowsSorting={col.allowsSorting}
              className={cx(
                dataTableStyles.th,
                "outline-none data-focus-visible:outline-2 data-focus-visible:-outline-offset-2 data-focus-visible:outline-solid data-focus-visible:outline-focus",
                col.align === "end" && "text-right",
                col.className,
              )}
            >
              {({ allowsSorting, sortDirection }) => (
                <span
                  className={cx(
                    "inline-flex items-center gap-1",
                    col.hideHeader && "sr-only",
                    allowsSorting && "cursor-default hover:text-fg",
                  )}
                >
                  {col.header}
                  {allowsSorting ? (
                    sortDirection === "ascending" ? (
                      <CaretUp size={12} weight="bold" aria-hidden="true" />
                    ) : sortDirection === "descending" ? (
                      <CaretDown size={12} weight="bold" aria-hidden="true" />
                    ) : (
                      <CaretDown size={12} aria-hidden="true" className="opacity-0" />
                    )
                  ) : null}
                </span>
              )}
            </Column>
          )}
        </TableHeader>
        <TableBody
          items={rows}
          renderEmptyState={renderEmptyState ? () => <div className="px-4 py-8">{renderEmptyState()}</div> : undefined}
        >
          {(row) => (
            <Row id={row.id} columns={columns} className={cx(rowStyle, onRowAction && "cursor-default")}>
              {(col) => (
                <Cell className={cx(dataTableStyles.td, col.align === "end" && "text-right", col.className)}>
                  {col.cell(row)}
                </Cell>
              )}
            </Row>
          )}
        </TableBody>
      </Table>
    </div>
  );
}

/** Cold-load placeholder in the table's frame, one skeleton row per expected row. */
export function TableSkeleton({ rows = 6, columns = 4 }: { rows?: number; columns?: number }) {
  return (
    <div role="status" aria-busy="true" className={dataTableStyles.frame}>
      <span className="sr-only">{m.common_loading()}</span>
      <div aria-hidden="true">
        <div className={cx(dataTableStyles.head, "flex h-8 items-center gap-6 px-3")}>
          {Array.from({ length: columns }, (_, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: static placeholder
            <Skeleton key={i} className={cx("h-2 w-16", i === 0 && "w-24")} />
          ))}
        </div>
        {Array.from({ length: rows }, (_, r) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: static placeholder
          <div key={r} className={cx(dataTableStyles.row, "flex h-14 items-center gap-6 px-3")}>
            {Array.from({ length: columns }, (_, c) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: static placeholder
              <div key={c} className={cx("flex items-center gap-2.5", c === 0 ? "w-48" : "w-20")}>
                {c === 0 ? <Skeleton className="size-6 shrink-0 rounded-full" /> : null}
                <div className="flex flex-1 flex-col gap-1.5">
                  <Skeleton className={cx("h-2.5", c === 0 ? "w-3/4" : "w-full")} />
                  {c === 0 ? <Skeleton className="h-2 w-1/2" /> : null}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
